import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { PARTNERS } from "@/data/partners";
import { trackEvent } from "@/lib/analytics";

// Official logo in original colours and proportions, or a clean text
// treatment when no official logo asset is available (per owner instruction —
// never recreate or fake a logo).
function PartnerMark({ partner, height = "h-16" }) {
  if (partner.logo) {
    return (
      <img src={partner.logo} alt={partner.logoAlt} loading="lazy" decoding="async"
        className={`${height} w-auto max-w-full object-contain`} />
    );
  }
  return (
    <span className={`font-display font-bold tracking-[0.18em] text-navy text-xl inline-flex items-center ${height}`}>
      {partner.name}
    </span>
  );
}

export function PartnerCard({ partner, compact = false }) {
  return (
    <div className="bg-white border border-line rounded-[16px] p-6 sm:p-8 flex flex-col items-center text-center hover:border-blue transition-colors"
      data-testid={`partner-card-${partner.name.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="h-20 flex items-center justify-center mb-4 px-4">
        <PartnerMark partner={partner} />
      </div>
      <span className="font-mono-jb text-[0.62rem] tracking-[0.14em] uppercase text-blue bg-blue-50 px-2 py-1 rounded mb-3">{partner.label}</span>
      {partner.logo && <h3 className="font-display text-lg text-ink mb-1.5">{partner.name}</h3>}
      {partner.description && !compact && (
        <p className="text-[0.88rem] text-muted mb-5 max-w-[42ch]">{partner.description}</p>
      )}
      {compact && <div className="mb-2" />}
      {partner.url && (
        <a href={partner.url} target="_blank" rel="noopener noreferrer"
          onClick={() => trackEvent("partner_click", { partner: partner.name })}
          data-testid={`partner-visit-${partner.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
          className="mt-auto inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-blue hover:underline min-h-[44px]">
          Visit Website <ExternalLink size={14} />
        </a>
      )}
    </div>
  );
}

// Compact homepage block — restrained so inspection bookings stay the focus.
export function PartnersBlock() {
  return (
    <section className="py-16 lg:py-20 bg-white border-t border-line" data-testid="partners-block">
      <div className="wrap">
        <SectionHead center eyebrow="Partner network" title="Our Partners"
          sub="Meet the businesses in Fedari's partner network." />
        <div className="grid sm:grid-cols-2 gap-5 max-w-[720px] mx-auto">
          {PARTNERS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06}><PartnerCard partner={p} compact /></Reveal>
          ))}
        </div>
        <div className="text-center mt-8">
          <p className="text-bodytext text-[0.95rem] mb-3">Interested in working with Fedari?</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link to="/partners#become-a-partner" data-testid="partners-block-cta"
              className="inline-flex items-center gap-2 bg-blue text-white px-6 py-3 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
              Become a Partner <ArrowRight size={15} />
            </Link>
            <Link to="/partners" data-testid="partners-block-link" className="text-blue font-semibold text-[0.9rem] hover:underline min-h-[44px] inline-flex items-center">
              View Our Partners
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
